import { useEffect, useState } from "react";
import {
  CLOUD_PROVIDERS,
  CLOUD_PROVIDER_DISPLAY,
  CLOUD_PROVIDER_KEY_HINT,
  type CloudProvider,
  type Settings,
  activeKey,
  effectiveModelConfig,
  isReadyForCloud,
} from "../data/types";
import {
  setApiKey,
  setCloudEnabled,
  setHuggingFaceToken,
  setProvider,
} from "../data/settingsStore";
import { createCloudEngine } from "../engine/cloudEngine";
import { createLocalEngine, isWebGpuAvailable } from "../engine/localEngine";
import type { LlmEngine, LlmResult } from "../engine/types";
import { type ModelInfo } from "../data/modelStore";
import { formatBytes } from "../ui/format";

interface Props {
  settings: Settings;
  onChange: (next: Settings) => void;
  onBack: () => void;
  onOpenModel: () => void;
  onOpenDebug: () => void;
  installedModel: ModelInfo | null;
}

export function SettingsScreen(props: Props) {
  const { settings, onChange } = props;

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Settings</h1>
        <span className="w-16" />
      </header>

      <main className="flex flex-1 flex-col gap-6 px-4 py-4">
        <CloudToggleSection
          settings={settings}
          onToggle={(enabled) => onChange(setCloudEnabled(settings, enabled))}
        />
        <hr className="border-black/5" />

        <ProviderSection
          settings={settings}
          enabled={settings.cloudEnabled}
          onSelect={(provider) => onChange(setProvider(settings, provider))}
        />
        <hr className="border-black/5" />

        <ApiKeysSection
          settings={settings}
          enabled={settings.cloudEnabled}
          onChange={(provider, key) => onChange(setApiKey(settings, provider, key))}
        />

        <PrivacyNote />

        <hr className="border-black/5" />

        <HuggingFaceSection
          token={settings.huggingFaceToken}
          onChange={(token) => onChange(setHuggingFaceToken(settings, token))}
        />

        <hr className="border-black/5" />

        <ModelLinkSection
          settings={settings}
          installed={props.installedModel}
          onOpenModel={props.onOpenModel}
        />

        <hr className="border-black/5" />
        <TestPanels settings={settings} installedModel={props.installedModel} />

        <hr className="border-black/5" />
        <section className="flex flex-col gap-2">
          <h2 className="text-base font-semibold">Diagnostics</h2>
          <p className="text-sm text-ink-muted">
            Live log of model load + inference, plus a self-check that probes the on-disk model
            file's first 32 bytes so we can tell whether MediaPipe is rejecting the format vs the
            file being something else entirely.
          </p>
          <button className="btn-ghost self-start" onClick={props.onOpenDebug}>
            Open debug log
          </button>
        </section>
      </main>
    </>
  );
}

function CloudToggleSection(props: { settings: Settings; onToggle: (next: boolean) => void }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Cloud LLM</h2>
      <p className="text-sm text-ink-muted">
        On-device Gemma is the default. Turn this on to use a cloud provider you supply a key for —
        useful when context exceeds what the local model can hold.
      </p>
      <label className="flex items-center justify-between gap-4 rounded-xl bg-surface p-3 ring-1 ring-black/5">
        <span className="text-sm font-medium">
          {props.settings.cloudEnabled ? "Enabled" : "Disabled"}
        </span>
        <Toggle
          checked={props.settings.cloudEnabled}
          onChange={(c) => props.onToggle(c)}
        />
      </label>
    </section>
  );
}

function ProviderSection(props: {
  settings: Settings;
  enabled: boolean;
  onSelect: (provider: CloudProvider) => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Provider</h2>
      <div className="flex flex-col gap-2">
        {CLOUD_PROVIDERS.map((provider) => {
          const selected = props.settings.selectedProvider === provider;
          return (
            <label
              key={provider}
              className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm ring-1 ring-black/5 ${
                selected ? "bg-accent-soft" : "bg-surface"
              } ${props.enabled ? "" : "opacity-50"}`}
            >
              <input
                type="radio"
                name="provider"
                disabled={!props.enabled}
                checked={selected}
                onChange={() => props.onSelect(provider)}
                className="accent-accent"
              />
              <span className="font-medium">{CLOUD_PROVIDER_DISPLAY[provider]}</span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

function ApiKeysSection(props: {
  settings: Settings;
  enabled: boolean;
  onChange: (provider: CloudProvider, key: string) => void;
}) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">API keys</h2>
      <div className="flex flex-col gap-3">
        {CLOUD_PROVIDERS.map((provider) => (
          <ApiKeyField
            key={provider}
            provider={provider}
            value={props.settings.apiKeys[provider] ?? ""}
            enabled={props.enabled}
            onChange={(key) => props.onChange(provider, key)}
          />
        ))}
      </div>
    </section>
  );
}

function ApiKeyField(props: {
  provider: CloudProvider;
  value: string;
  enabled: boolean;
  onChange: (key: string) => void;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-muted">
        {CLOUD_PROVIDER_DISPLAY[props.provider]}
      </span>
      <div className="flex items-center gap-2">
        <input
          className="input"
          type={revealed ? "text" : "password"}
          autoComplete="off"
          spellCheck={false}
          placeholder={CLOUD_PROVIDER_KEY_HINT[props.provider]}
          value={props.value}
          disabled={!props.enabled}
          onChange={(e) => props.onChange(e.target.value)}
        />
        <button
          type="button"
          className="btn-ghost"
          disabled={!props.enabled || props.value.length === 0}
          onClick={() => setRevealed((r) => !r)}
        >
          {revealed ? "Hide" : "Show"}
        </button>
      </div>
    </label>
  );
}

function HuggingFaceSection(props: { token: string; onChange: (next: string) => void }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Hugging Face access</h2>
      <p className="text-sm text-ink-muted">
        Gemma is gated. Accept the license on the model's Hugging Face page, then paste a read
        token from huggingface.co/settings/tokens below — the model download uses it as an
        Authorization header.
      </p>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-ink-muted">Read token</span>
        <div className="flex items-center gap-2">
          <input
            className="input"
            type={revealed ? "text" : "password"}
            autoComplete="off"
            spellCheck={false}
            placeholder="hf_…"
            value={props.token}
            onChange={(e) => props.onChange(e.target.value)}
          />
          <button
            type="button"
            className="btn-ghost"
            disabled={props.token.length === 0}
            onClick={() => setRevealed((r) => !r)}
          >
            {revealed ? "Hide" : "Show"}
          </button>
        </div>
      </label>
    </section>
  );
}

function ModelLinkSection(props: {
  settings: Settings;
  installed: ModelInfo | null;
  onOpenModel: () => void;
}) {
  const config = effectiveModelConfig(props.settings);
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">On-device model</h2>
      {props.installed ? (
        <p className="text-sm">
          <span className="font-medium text-accent">
            Installed ({formatBytes(props.installed.sizeBytes)})
          </span>{" "}
          — {props.installed.fileName}
        </p>
      ) : (
        <p className="text-sm text-ink-muted">
          Not installed. Default model: {config.displayName}. Download to run on-device.
        </p>
      )}
      <button className="btn-ghost self-start" onClick={props.onOpenModel}>
        {props.installed ? "Manage model" : "Open model setup"}
      </button>
    </section>
  );
}

function PrivacyNote() {
  return (
    <div className="rounded-xl bg-surface p-3 text-xs text-ink-muted ring-1 ring-black/5">
      Keys and tokens stay in this browser's storage. They're sent only to the provider you select,
      never to us.
    </div>
  );
}

function Toggle(props: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.checked}
      onClick={() => props.onChange(!props.checked)}
      className={`relative h-6 w-11 rounded-full transition ${
        props.checked ? "bg-accent" : "bg-black/15"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          props.checked ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}

const TEST_PROMPT = 'Reply with the exact text "Engine ready" and nothing else.';

type TestState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "success"; text: string; source: string }
  | { kind: "failure"; message: string; source: string };

function TestPanels(props: { settings: Settings; installedModel: ModelInfo | null }) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-base font-semibold">Test engines</h2>
      <TestPanel
        label="Cloud"
        engineFactory={
          isReadyForCloud(props.settings)
            ? () =>
                createCloudEngine(
                  props.settings.selectedProvider,
                  activeKey(props.settings),
                )
            : null
        }
        disabledHint="Enable cloud and add an API key above to run a test."
        resetKey={JSON.stringify({ ...props.settings, apiKeys: undefined })}
      />
      <TestPanel
        label="On-device (Gemma)"
        engineFactory={
          props.installedModel && isWebGpuAvailable()
            ? () => createLocalEngine(props.installedModel!.fileName)
            : null
        }
        disabledHint={
          !isWebGpuAvailable()
            ? "WebGPU isn't available in this browser."
            : "Install the on-device model above to test."
        }
        extraHint={
          props.installedModel
            ? "First run loads the model into GPU memory — expect a few seconds before the reply."
            : null
        }
        resetKey={props.installedModel?.fileName ?? "none"}
      />
    </section>
  );
}

function TestPanel(props: {
  label: string;
  engineFactory: (() => LlmEngine) | null;
  disabledHint: string;
  extraHint?: string | null;
  resetKey: string;
}) {
  const [state, setState] = useState<TestState>({ kind: "idle" });

  useEffect(() => {
    setState({ kind: "idle" });
  }, [props.resetKey]);

  const ready = props.engineFactory !== null;
  const handleTest = async () => {
    if (!props.engineFactory) return;
    setState({ kind: "running" });
    const engine = props.engineFactory();
    const result: LlmResult = await engine.complete({
      messages: [{ role: "user", content: TEST_PROMPT }],
      maxTokens: 512,
      temperature: 0,
    });
    if (result.kind === "success") {
      setState({ kind: "success", text: result.text, source: result.source });
    } else {
      setState({ kind: "failure", message: result.message, source: result.source });
    }
  };

  return (
    <div className="card flex flex-col gap-2 p-4">
      <div className="text-sm font-semibold">{props.label}</div>
      {!ready ? (
        <div className="text-xs text-ink-muted">{props.disabledHint}</div>
      ) : props.extraHint ? (
        <div className="text-xs text-ink-muted">{props.extraHint}</div>
      ) : null}
      <button
        className="btn-primary"
        disabled={!ready || state.kind === "running"}
        onClick={handleTest}
      >
        {state.kind === "running" ? "Sending test prompt…" : "Test connection"}
      </button>
      {state.kind === "success" && (
        <ResultCard tone="success" heading={`${state.source} replied`} body={state.text} />
      )}
      {state.kind === "failure" && (
        <ResultCard tone="error" heading={`${state.source} failed`} body={state.message} />
      )}
    </div>
  );
}

function ResultCard(props: {
  tone: "success" | "error";
  heading: string;
  body: string;
}) {
  const isError = props.tone === "error";
  return (
    <div
      className={`rounded-xl p-3 text-sm ring-1 ${
        isError
          ? "bg-rose-50 text-rose-900 ring-rose-200"
          : "bg-accent-soft text-ink ring-accent/20"
      }`}
    >
      <div
        className={`text-xs font-semibold ${
          isError ? "text-rose-700" : "text-accent"
        }`}
      >
        {props.heading}
      </div>
      <div className="mt-1 whitespace-pre-wrap">{props.body}</div>
    </div>
  );
}
